import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react'

const cx = (...classes: (string | undefined | false | null)[]) => classes.filter(Boolean).join(' ')

interface WrapperBaseProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
  $sidebar?: boolean
  $isMainPage?: boolean
  $isnotauthorized?: boolean
  $collapsed?: boolean
  $ishome?: boolean
  theme?: Record<string, string>
}

const WrapperBase = ({ ref, className, style, $sidebar, $isMainPage, $isnotauthorized, $collapsed, $ishome, theme, ...props }: WrapperBaseProps) => {
  const bgColor = $isMainPage && !$isnotauthorized
    ? 'var(--main-page-sidebar-bg)'
    : $sidebar
      ? 'var(--child-menu-bg)'
      : 'var(--sidebar-bg)'

  return (
    <div
      ref={ref}
      className={cx('max-w-full h-screen backdrop-blur-[42.5px]', className)}
      style={{
        backgroundColor: bgColor,
        paddingTop: $sidebar ? 15 : 20,
        width: $collapsed ? 64 : 250,
        ...style,
      }}
      {...props}
    />
  )
}

interface WrapperTopProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
  $isCollapsed?: boolean
}

const WrapperTop = ({ ref, className, style, $isCollapsed, ...props }: WrapperTopProps) => (
  <div
    ref={ref}
    className={cx(
      'flex items-center w-full px-[19px] py-2.5 text-(--sidebar-text) [&_svg]:!w-[18px] [&_svg]:!h-[18px] [&_.menu-title]:uppercase [&_.menu-title]:text-white [&_.menu-header]:flex [&_.menu-header]:gap-1.5 [&_.menu-header]:pl-[19px] [&_.menu-header]:items-center [&_.menu-header]:cursor-pointer [&_.sidebar-title]:text-[#456b89] [&_.sidebar-title]:text-[13px] [&_.sidebar-title]:font-medium [&_.sidebar-title]:leading-7 [&_.sidebar-title]:px-3',
      className,
    )}
    style={{
      height: $isCollapsed ? 96 : 48,
      gap: $isCollapsed ? 10 : 5,
      flexDirection: !$isCollapsed ? 'row' : 'column',
      justifyContent: $isCollapsed ? 'flex-start' : 'space-between',
      ...style,
    }}
    {...props}
  />
)

interface WrapperBodyProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
  $collapsed?: boolean
  collapsed?: boolean
}

const WrapperBody = ({ ref, className, style, $collapsed, collapsed, children, ...props }: WrapperBodyProps) => {
  const isCollapsed = $collapsed ?? collapsed
  return (
    <div
      ref={ref}
      className={cx('h-fit flex flex-wrap shortcut-wrapper-body', className)}
      style={style}
      {...props}
    >
      <style>{`
        .shortcut-wrapper-body > .column {
          height: fit-content;
          padding: ${!isCollapsed ? '19px' : '19px 2px'};
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
      `}</style>
      {children}
    </div>
  )
}

interface DivProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
}

const WrapperSidebarBody = ({ ref, className, ...props }: DivProps) => (
  <div
    ref={ref}
    className={cx(
      'h-fit flex flex-col gap-5 px-5 py-3.5 max-w-[250px] text-[13px] font-medium leading-7',
      '[&_.column-sidebar]:h-fit [&_.column-sidebar]:w-48 [&_.column-sidebar]:flex [&_.column-sidebar]:flex-col [&_.column-sidebar]:gap-1',
      '[&_.column-sidebar-title]:flex [&_.column-sidebar-title]:flex-col [&_.column-sidebar-title]:items-start [&_.column-sidebar-title]:gap-1 [&_.column-sidebar-title]:self-stretch [&_.column-sidebar-title]:text-[rgba(69,107,137,0.85)] [&_.column-sidebar-title]:text-[13px] [&_.column-sidebar-title]:font-normal [&_.column-sidebar-title]:leading-[22px]',
      '[&_.column-sidebar-link]:flex [&_.column-sidebar-link]:flex-col [&_.column-sidebar-link]:items-start [&_.column-sidebar-link]:gap-1 [&_.column-sidebar-link]:self-stretch [&_.column-sidebar-link]:text-[#0090ff] [&_.column-sidebar-link]:text-[13px] [&_.column-sidebar-link]:font-normal [&_.column-sidebar-link]:leading-[22px]',
      '[&_a]:text-[#0090ff]',
      className,
    )}
    {...props}
  />
)

export const Wrapper = Object.assign(WrapperBase, {
  Top: WrapperTop,
  Body: WrapperBody,
  SidebarBody: WrapperSidebarBody,
})
